import React from "react";
import { Typography, Card, Tag, Descriptions, Alert, Empty, Divider } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined, UserOutlined, ClockCircleOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import MvtFlightUpdateHandler from "../mvt/MvtFlightUpdateHandler";
import NewAgentResultViewer from "./NewAgentResultViewer";
import ExecutionResultsViewer from "./ExecutionResultsViewer";

const { Title, Text, Paragraph } = Typography;

/**
 * 已处理行动项的只读视图组件
 */
const ReadOnlyActionDetailViewer = ({ actionItem, currentFlightData }) => {
    const resolverUser = actionItem.resolver_user_id ? `用户ID: ${actionItem.resolver_user_id}` : "系统自动处理";

    const resolvedAt = actionItem.resolved_at ? dayjs(actionItem.resolved_at).format("YYYY-MM-DD HH:mm:ss") : "未知时间";

    const userRemarks = actionItem.user_remarks || "无备注";

    // V4.1 更新：支持部分完成状态
    const actionTypeMap = {
        USER_CONFIRMED: { label: "用户确认采纳", color: "success", icon: <CheckCircleOutlined /> },
        USER_MODIFIED_CONFIRMED: { label: "用户修改后采纳", color: "processing", icon: <CheckCircleOutlined /> },
        USER_REJECTED: { label: "用户拒绝", color: "default", icon: <CloseCircleOutlined /> },
        AUTO_CONFIRMED_PROCESSED: { label: "系统自动处理", color: "cyan", icon: <CheckCircleOutlined /> },
    };

    // V4.1 更新：优先使用 status 字段判断状态
    const statusKey = actionItem.resolution_status;
    const actionTypeInfo = actionTypeMap[statusKey] || {
        label: actionItem.status || actionItem.resolution_status || "未知状态",
        color: "default",
        icon: null,
    };

    // 获取行动项核心内容渲染器
    const renderActionContent = () => {
        // 检查是否为新Agent（V4.1规范）
        const isNewAgent = actionItem.suggested_payload && actionItem.suggested_payload.version === "4.1" && actionItem.agent_type && actionItem.agent_type !== "MVT_AGENT";

        // 新Agent使用NewAgentResultViewer
        if (isNewAgent) {
            return <NewAgentResultViewer actionItem={actionItem} currentFlightData={currentFlightData} />;
        }

        // MVT Agent的处理逻辑 - 优先显示详细的MVT信息
        const getActionType = () => {
            if (actionItem.suggested_payload?.system_operations?.length > 0) {
                return actionItem.suggested_payload.system_operations[0].parameters?.change_type;
            }
            return actionItem.suggested_payload?.action_type;
        };

        const actionType = getActionType();
        if (actionItem.source_type === "FLIGHT_MESSAGE_MVT") {
            if (actionType && actionType.includes("UPDATE_OPERATION_FLIGHT_FROM_MVT")) {
                return (
                    <div>
                        <Alert message="此行动项已处理完成，以下为当时的处理数据" type="info" showIcon style={{ marginBottom: 16 }} />
                        <MvtFlightUpdateHandler
                            actionItem={actionItem}
                            currentFlightData={currentFlightData}
                            onParametersChange={() => {}} // 只读模式不需要响应变更
                        />
                    </div>
                );
            } else {
                // 针对没有具体处理数据的情况，显示简单摘要
                return <Alert message="此MVT行动项已处理完成" description={`处理方式: ${actionTypeInfo.label}`} type="info" showIcon />;
            }
        }

        // V3.0 通用：检查是否有execution_results（非MVT类型）
        const executionResults = actionItem.additional_info?.execution_results;
        if (executionResults && Array.isArray(executionResults) && executionResults.length > 0) {
            return (
                <div>
                    <Alert message="此行动项已执行完成" description={`处理方式: ${actionTypeInfo.label}`} type="info" showIcon style={{ marginBottom: 16 }} />
                    <ExecutionResultsViewer executionResults={executionResults} showDetails={true} />
                </div>
            );
        }

        // 默认情况
        return <Empty description="暂无详细处理数据可显示" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
    };

    return (
        <div>
            {/* 行动项处理状态信息 */}
            <Card style={{ marginBottom: 16 }}>
                <Tag icon={actionTypeInfo.icon} color={actionTypeInfo.color} style={{ marginBottom: 8, fontSize: "14px", padding: "4px 8px" }}>
                    {actionTypeInfo.label}
                </Tag>

                <Descriptions column={2} size="small" bordered>
                    <Descriptions.Item label="处理人" span={1}>
                        <UserOutlined /> {resolverUser}
                    </Descriptions.Item>
                    <Descriptions.Item label="处理时间" span={1}>
                        <ClockCircleOutlined /> {resolvedAt}
                    </Descriptions.Item>
                    <Descriptions.Item label="处理备注" span={2}>
                        {userRemarks}
                    </Descriptions.Item>
                </Descriptions>
            </Card>

            {/* 行动项具体内容 */}
            <Divider orientation="left">处理的内容</Divider>

            {renderActionContent()}
        </div>
    );
};

export default ReadOnlyActionDetailViewer;
